import { AppConfig } from '../services/app-config.service'
import * as DomainObject from './domain-object.model'
import * as DomainType from './domain-type.model'
import * as Traits from './traits'

export type T = Graph
type Graph = Node[]

export type Node = {
  data: DomainObject.T
  type: DomainType.T | null
  links: Links
  linksFlattened: Links
}
export type Links = Record<DomainObject.Relation, Node[]>

export type DisplayableNode = Node & {type: DomainType.Global<Traits.DisplayableInCharts.T>}
export type SupplierMeterNode = DisplayableNode & {
  data: DomainObject.T & {
    attributes: {
      name: string,
      total: DisplayableAttribute,
      tariffs: DisplayableAttribute[]
    }
  }
}
export type SinglePropertyNode = DisplayableNode & {
  data: DomainObject.T & {
    attributes: {
      name: string,
      value: DisplayableAttribute
    }
  }
}

export type DisplayableAttribute = {
  id: string,
  label: string,
  type: 'SimpleCalculation' | 'SimpleMeasure' | 'CumulativeConsumptionMeasure',
  quantity?: string,
  step?: string,
  unit: string,
  baseUnit?: string, 
  displayUnit?: string
}

export const isNode = (domainNode: any) : domainNode is Node => domainNode.data != null && domainNode.links != null
export const isLeaf = (domainNode: Node) : boolean => domainNode.links?.child?.length === 0
export const isDisplayable = (domainNode: Node) : domainNode is DisplayableNode => {
  const nodeType = typeOfNode(domainNode)
  return nodeType != null && DomainType.isConfigured(nodeType) && Traits.DisplayableInCharts.isDisplayableInCharts(DomainType.traitsOf(nodeType))
}
export const asDisplayable = (domainNode: Node) : DisplayableNode | null => isDisplayableAsSupplierMeter(domainNode) ? domainNode : null

export const isDisplayableAsSupplierMeter = (domainNode: Node): domainNode is SupplierMeterNode => {
  const nodeType = typeOfNode(domainNode)
  return nodeType != null && DomainType.isConfigured(nodeType) && Traits.DisplayableInCharts.isSupplierMeter(DomainType.traitsOf(nodeType))
}
export const asDisplayableAsSupplierMeter = (domainNode: Node): SupplierMeterNode | null => isDisplayableAsSupplierMeter(domainNode) ? domainNode : null
export const isDisplayableAsSingleProperty = (domainNode: Node): domainNode is SinglePropertyNode => {
  const nodeType = typeOfNode(domainNode)
  return nodeType != null && DomainType.isConfigured(nodeType) && Traits.DisplayableInCharts.isSingleProperty(DomainType.traitsOf(nodeType))
}
export const asDisplayableAsSingleProperty = (domainNode: Node): SinglePropertyNode | null => isDisplayableAsSingleProperty(domainNode) ? domainNode : null

export const resolveDisplayableType = (domainNode: Node): Traits.DisplayableInCharts.Template | null =>
  isDisplayableAsSingleProperty(domainNode) ? 'single-property'
    : isDisplayableAsSupplierMeter(domainNode) ? 'supplier-meter'
      : null

export const dataOfNode = ({ data }: Node): DomainObject.T => data
export const typeOfNode = <SpecificNode extends Node>({ type }: SpecificNode): SpecificNode['type'] => type
export const domainObjectOfNode = dataOfNode
export const idOfNode = (domainNode: Node): string => DomainObject.idOf(dataOfNode(domainNode))
export const labelOfNode = (domainNode: Node): string => DomainObject.labelOf(dataOfNode(domainNode))
export const attributesOfDisplayable = <SomeDisplayableNode extends DisplayableNode>(
  domainNode: SomeDisplayableNode
):SomeDisplayableNode['data']['attributes'] => {
  const domainObject = dataOfNode(domainNode)
  const attributes = DomainObject.attributesOf(domainObject)
  if(isDisplayableAsSupplierMeter(domainNode)) {
    return {
      ...(attributes.name && { name: attributes.name }),
      total  : { ...attributes.total, label: DomainObject.labelOf(domainObject), type: 'SimpleCalculation', unit: attributes.total.unit || 'none' },
      tariffs: attributes.tariffs.map((tariff) => ({ ...tariff, type: tariff.toGridType === 'INDEX2CONS' ? 'CumulativeConsumptionMeasure' : 'SimpleMeasure', unit: tariff.unit || 'none' }))
    }
  } else if (isDisplayableAsSingleProperty(domainNode)) {
    return { ...(attributes.name && { name: attributes.name }), value: { ...attributes.value, label: DomainObject.labelOf(domainObject), type: 'SimpleCalculation' } }
  } else {
    return []
  } 
}
export const displayableAttributesOfNode = (domainNode: DisplayableNode): DisplayableAttribute[] => {
  const displayableAttributes = attributesOfDisplayable(domainNode)
  if(isDisplayableAsSupplierMeter(domainNode)) {
    return [
      displayableAttributes.total,
      ...displayableAttributes.tariffs
    ]
  } else if (isDisplayableAsSingleProperty(domainNode)) {
    return [ displayableAttributes.value ]
  } else {
    return []
  }
}

export const resolveDisplayable = (domainGraph : Graph, seriesId: DisplayableAttribute['id']): DisplayableNode =>
  domainGraph.find(node => isDisplayable(node) && displayableAttributesOfNode(node).find(({ id })=>seriesId === id)) as DisplayableNode

export const isDisplayableAttributeIntegral = ({ quantity }: DisplayableAttribute): boolean => quantity ? [ 'E', 'V' ].includes(quantity) : true

const getRecursiveRelationNodes = (domainNode: Node, relation: string): Graph => {
  const links = (domainNode.links[relation] ?? [])
  return [
    ...links,
    ...links.map(relatedNode => getRecursiveRelationNodes(relatedNode, relation)).flat()
  ]
}

const nodeFromDomainObject = (domainObject: DomainObject.T, domainTypes: DomainType.T[]): Node => ({
  links: {
    child : [],
    parent: [],
  },
  linksFlattened: {
    child : [],
    parent: [],
  },
  data: domainObject,
  type: DomainType.forDomainObject(domainObject, domainTypes)
})

export const fromDomainObjects = (domainObjects: DomainObject.T[], appConfig: AppConfig): Graph => {
  const domainTypeList: DomainType.T[] = DomainType.collectFromConfig(appConfig)
  const domainNodes: Node[] = domainObjects.map(domainObject => {
    try {
      return nodeFromDomainObject(domainObject, domainTypeList)
    } catch (error) {
      console.warn('failed to parse domain node:', error)
      return null
    }
  }).filter((node): node is Node => node !== null)
  const domainNodesMap: { [id: string]: number}  = domainNodes.reduce((acc, node, index) => {
    // eslint-disable-next-line no-param-reassign
    acc[idOfNode(node)] = index
    return acc
  }, {})
  // We create All direct links
  domainNodes.forEach(domainNode => {
    DomainObject.linksOf(dataOfNode(domainNode)).forEach(link => {
      // Use our mapping to locate the parent element in our data array
      if (domainNodesMap[link.to] === undefined) return
      const parentEl = domainNodes[domainNodesMap[link.to]]
      const thisNode = domainNodes[domainNodesMap[idOfNode(domainNode)]]
      // Add parent to our current element's `parent` array
      thisNode.links.parent.push(parentEl)
      // Add our current el to its parent's `child` array
      parentEl.links.child.push(domainNode)
    })
  })

  // We flatten all links for later facilitating later grouping between separated levels
  domainNodes.forEach(domainNode => {
    Object.keys(domainNode.links).forEach(relation => {
      const result = getRecursiveRelationNodes(domainNode, relation)
      domainNodes[domainNodesMap[idOfNode(domainNode)]].linksFlattened[relation] = result
    })
  })
  return domainNodes
}
